// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {isDesktop} from '@app/features/ui/utils/NativeUtils';
import {
	createMicTestAudioGraph,
	type MicTestAudioGraph,
} from '@app/features/user/components/modals/tabs/hooks/MicTestAudioGraph';
import {MicrophonePermissionDeniedModal} from '@app/features/voice/components/alerts/MicrophonePermissionDeniedModal';
import {createVoiceDiagnosticLogger} from '@app/features/voice/diagnostics/VoiceDiagnostics';
import MediaPermission from '@app/features/voice/state/MediaPermission';
import {buildDeepFilterAudioChain, type DeepFilterAudioChain} from '@app/features/voice/utils/DeepFilterNoiseProcessor';
import {ensureNativePermission} from '@app/features/voice/utils/NativePermissions';
import {
	applyContentHintToTrack,
	resolveVoiceProcessing,
	type VoiceProcessingMode,
} from '@app/features/voice/utils/VoiceProcessingProfile';
import {
	boostedVoiceVolumePercentToTrackVolume,
	inputVoiceVolumePercentToGain,
} from '@app/features/voice/utils/VoiceVolumeUtils';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const logger = new Logger('useMicTest');

const diagnostics = createVoiceDiagnosticLogger('app.useMicTest');
interface SinkableAudioContext extends AudioContext {
	setSinkId?: (sinkId: string | {type: 'none'}) => Promise<void>;
}

const MIC_LEVEL_DIAGNOSTIC_FRAMES = 90;
const MIC_LEVEL_ACTIVE_RMS = 0.01;

const TEST_AUDIO_CONTEXT_SAMPLE_RATE = 48000;
export interface MicTestSettings {
	inputDeviceId: string;
	outputDeviceId: string;
	inputVolume: number;
	outputVolume: number;
	echoCancellation: boolean;
	noiseSuppression: boolean;
	autoGainControl: boolean;
	deepFilterNoiseSuppression: boolean;
	voiceProcessingMode: VoiceProcessingMode;
}
function summarizeSettings(settings: MicTestSettings): Record<string, unknown> {
	return {
		inputDevice: settings.inputDeviceId === 'default' ? 'default' : 'custom',
		outputDevice: settings.outputDeviceId === 'default' ? 'default' : 'custom',
		inputVolume: settings.inputVolume,
		outputVolume: settings.outputVolume,
		voiceProcessingMode: settings.voiceProcessingMode,
	};
}
function summarizeError(error: unknown): Record<string, unknown> {
	if (error instanceof Error) {
		return {
			name: error.name,
			message: error.message,
		};
	}
	return {message: String(error)};
}
function normalizeOutputDeviceId(deviceId: string): string {
	return deviceId === 'default' ? '' : deviceId;
}
export const useMicTest = (settings: MicTestSettings) => {
	const [isTesting, setIsTesting] = useState(false);
	const [level, setLevel] = useState(0);
	const audioContextRef = useRef<AudioContext | null>(null);
	const graphRef = useRef<MicTestAudioGraph | null>(null);
	const playbackDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
	const audioElementRef = useRef<HTMLAudioElement | null>(null);
	const micStreamRef = useRef<MediaStream | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const timeDomainDataRef = useRef<Float32Array<ArrayBuffer> | null>(null);
	const isStartingRef = useRef(false);
	const restartPendingRef = useRef(false);
	const activeCaptureSignatureRef = useRef<string | null>(null);
	const micSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
	const deepFilterChainRef = useRef<DeepFilterAudioChain | null>(null);
	const levelDiagnosticRef = useRef({
		frameCount: 0,
		hasLoggedActivity: false,
		peakRms: 0,
	});
	const micExplicitlyDenied = MediaPermission.microphoneExplicitlyDenied;
	const captureSignature = useMemo(
		() =>
			JSON.stringify({
				inputDeviceId: settings.inputDeviceId,
				outputDeviceId: settings.outputDeviceId,
				echoCancellation: settings.echoCancellation,
				noiseSuppression: settings.noiseSuppression,
				autoGainControl: settings.autoGainControl,
				deepFilterNoiseSuppression: settings.deepFilterNoiseSuppression,
				voiceProcessingMode: settings.voiceProcessingMode,
			}),
		[
			settings.autoGainControl,
			settings.deepFilterNoiseSuppression,
			settings.echoCancellation,
			settings.inputDeviceId,
			settings.noiseSuppression,
			settings.outputDeviceId,
			settings.voiceProcessingMode,
		],
	);
	const updateLevel = useCallback(() => {
		if (!graphRef.current || !timeDomainDataRef.current) {
			animationFrameRef.current = requestAnimationFrame(updateLevel);
			return;
		}
		graphRef.current.analyser.getFloatTimeDomainData(timeDomainDataRef.current);
		let sumOfSquares = 0;
		for (let i = 0; i < timeDomainDataRef.current.length; i++) {
			const sample = timeDomainDataRef.current[i];
			sumOfSquares += sample * sample;
		}
		const rms = Math.sqrt(sumOfSquares / timeDomainDataRef.current.length);
		const levelDiagnostic = levelDiagnosticRef.current;
		levelDiagnostic.frameCount += 1;
		levelDiagnostic.peakRms = Math.max(levelDiagnostic.peakRms, rms);
		if (
			!levelDiagnostic.hasLoggedActivity &&
			(rms > MIC_LEVEL_ACTIVE_RMS || levelDiagnostic.frameCount >= MIC_LEVEL_DIAGNOSTIC_FRAMES)
		) {
			levelDiagnostic.hasLoggedActivity = true;
			diagnostics.info('level.sampled', {
				active: levelDiagnostic.peakRms > MIC_LEVEL_ACTIVE_RMS,
				frameCount: levelDiagnostic.frameCount,
				peakRms: levelDiagnostic.peakRms,
			});
		}
		const MIN_DB = -60;
		const MAX_DB = 0;
		const db = 20 * Math.log10(Math.max(rms, 1e-10));
		const normalized = Math.max(0, Math.min(1, (db - MIN_DB) / (MAX_DB - MIN_DB)));
		setLevel(normalized);
		animationFrameRef.current = requestAnimationFrame(updateLevel);
	}, []);
	const stop = useCallback(() => {
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
		if (audioElementRef.current) {
			audioElementRef.current.pause();
			audioElementRef.current.srcObject = null;
			audioElementRef.current = null;
		}
		if (graphRef.current) {
			graphRef.current.source.disconnect();
			graphRef.current.analyser.disconnect();
			graphRef.current.inputGain.disconnect();
			graphRef.current.outputGain.disconnect();
			graphRef.current = null;
		}
		if (playbackDestinationRef.current) {
			playbackDestinationRef.current.disconnect();
			playbackDestinationRef.current.stream.getTracks().forEach((track) => track.stop());
			playbackDestinationRef.current = null;
		}
		if (deepFilterChainRef.current) {
			const chain = deepFilterChainRef.current;
			deepFilterChainRef.current = null;
			void chain.dispose().catch((error) => {
				logger.warn('Failed to dispose DeepFilter chain for mic test', error);
			});
		}
		if (micSourceNodeRef.current) {
			try {
				micSourceNodeRef.current.disconnect();
			} catch (error) {
				logger.debug('Failed to disconnect mic source node during stop', error);
			}
			micSourceNodeRef.current = null;
		}
		if (micStreamRef.current) {
			micStreamRef.current.getTracks().forEach((track) => track.stop());
			micStreamRef.current = null;
		}
		if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
			void audioContextRef.current.close();
			audioContextRef.current = null;
		}
		timeDomainDataRef.current = null;
		levelDiagnosticRef.current = {
			frameCount: 0,
			hasLoggedActivity: false,
			peakRms: 0,
		};
		setIsTesting(false);
		setLevel(0);
	}, []);
	const start = useCallback(async () => {
		if (isStartingRef.current) return;
		if (micExplicitlyDenied) {
			ModalCommands.push(modal(() => <MicrophonePermissionDeniedModal />));
			return;
		}
		isStartingRef.current = true;
		try {
			diagnostics.info('start.requested', summarizeSettings(settings));
			stop();
			if (isDesktop()) {
				const nativeResult = await ensureNativePermission('microphone');
				if (nativeResult === 'denied') {
					MediaPermission.markMicrophoneExplicitlyDenied();
					ModalCommands.push(modal(() => <MicrophonePermissionDeniedModal />));
					return;
				}
			}
			const profile = resolveVoiceProcessing(settings);
			const baseAudioConstraints: MediaTrackConstraints = {
				echoCancellation: profile.echoCancellation,
				noiseSuppression: profile.browserNoiseSuppression,
				autoGainControl: profile.autoGainControl,
			};
			const useExactDeviceId = settings.inputDeviceId !== 'default';
			const buildAudioConstraints = (exact: boolean): MediaTrackConstraints =>
				useExactDeviceId
					? {
							...baseAudioConstraints,
							deviceId: exact ? {exact: settings.inputDeviceId} : {ideal: settings.inputDeviceId},
						}
					: baseAudioConstraints;
			let stream: MediaStream;
			try {
				stream = await navigator.mediaDevices.getUserMedia({audio: buildAudioConstraints(true)});
			} catch (error) {
				if (!useExactDeviceId || !(error instanceof Error) || error.name !== 'OverconstrainedError') {
					throw error;
				}
				diagnostics.warn('getUserMedia.exact.fallback', {
					inputDevice: 'custom',
					error: summarizeError(error),
				});
				stream = await navigator.mediaDevices.getUserMedia({audio: buildAudioConstraints(false)});
			}
			micStreamRef.current = stream;
			const sourceTrack = stream.getAudioTracks()[0];
			if (!sourceTrack) {
				throw new Error('getUserMedia returned no audio tracks for mic test');
			}
			applyContentHintToTrack(sourceTrack, profile.contentHint);
			const audioContext = new AudioContext({sampleRate: TEST_AUDIO_CONTEXT_SAMPLE_RATE});
			audioContextRef.current = audioContext;
			if (audioContext.state === 'suspended') {
				await audioContext.resume();
			}
			const micSourceNode = audioContext.createMediaStreamSource(new MediaStream([sourceTrack]));
			micSourceNodeRef.current = micSourceNode;
			let processedTrackForGraph: MediaStreamTrack = sourceTrack;
			let deepFilterApplied = false;
			if (profile.deepFilter) {
				try {
					const chain = await buildDeepFilterAudioChain({audioContext});
					deepFilterChainRef.current = chain;
					micSourceNode.connect(chain.inputDestination);
					processedTrackForGraph = chain.processedTrack;
					deepFilterApplied = true;
					logger.info('Applied DeepFilterNet3 noise suppression for mic test');
				} catch (error) {
					logger.warn('Failed to apply DeepFilterNet3 for mic test, continuing without it', error);
					if (deepFilterChainRef.current) {
						const orphaned = deepFilterChainRef.current;
						deepFilterChainRef.current = null;
						await orphaned.dispose().catch((disposeError) => {
							logger.debug('Disposing DeepFilter chain after init failure threw', disposeError);
						});
					}
				}
			}
			const outputSinkId = normalizeOutputDeviceId(settings.outputDeviceId);
			const playbackDestination = audioContext.createMediaStreamDestination();
			playbackDestinationRef.current = playbackDestination;
			let playbackTarget: AudioNode = playbackDestination;
			let playbackMode: 'media-element' | 'audio-context-fallback' = 'media-element';
			let mediaElementSinkApplied = false;
			let audioContextSinkApplied = false;
			const processedStream = new MediaStream([processedTrackForGraph]);
			graphRef.current = createMicTestAudioGraph({
				audioContext,
				stream: processedStream,
				inputGain: inputVoiceVolumePercentToGain(settings.inputVolume),
				outputGain: boostedVoiceVolumePercentToTrackVolume(settings.outputVolume),
				playbackTarget,
			});
			timeDomainDataRef.current = new Float32Array(graphRef.current.analyser.fftSize);
			const audioElement = new Audio();
			audioElementRef.current = audioElement;
			audioElement.autoplay = true;
			audioElement.muted = false;
			audioElement.volume = 1;
			audioElement.srcObject = playbackDestination.stream;
			if (settings.outputDeviceId !== 'default' && typeof audioElement.setSinkId === 'function') {
				try {
					await audioElement.setSinkId(outputSinkId);
					mediaElementSinkApplied = true;
				} catch (error) {
					logger.warn('Failed to set mic test media element output device', error);
					diagnostics.warn('playback.mediaElementSetSinkId.failed', {
						outputDevice: 'custom',
						error: summarizeError(error),
					});
				}
			}
			try {
				await audioElement.play();
			} catch (error) {
				logger.warn('Failed to start mic test media element playback; falling back to AudioContext destination', error);
				diagnostics.warn('playback.mediaElementPlay.failed', {
					outputDevice: settings.outputDeviceId === 'default' ? 'default' : 'custom',
					error: summarizeError(error),
				});
				audioElement.pause();
				audioElement.srcObject = null;
				audioElementRef.current = null;
				playbackDestination.disconnect();
				playbackDestination.stream.getTracks().forEach((track) => track.stop());
				playbackDestinationRef.current = null;
				graphRef.current.outputGain.disconnect();
				playbackMode = 'audio-context-fallback';
				playbackTarget = audioContext.destination;
				const sinkableAudioContext = audioContext as SinkableAudioContext;
				if (sinkableAudioContext.setSinkId) {
					try {
						await sinkableAudioContext.setSinkId(outputSinkId);
						audioContextSinkApplied = true;
					} catch (sinkError) {
						logger.warn('Failed to set mic test AudioContext output device', sinkError);
						diagnostics.warn('playback.audioContextSetSinkId.failed', {
							outputDevice: settings.outputDeviceId === 'default' ? 'default' : 'custom',
							error: summarizeError(sinkError),
						});
					}
				}
				graphRef.current.outputGain.connect(playbackTarget);
				graphRef.current.playbackTarget = playbackTarget;
			}
			diagnostics.info('playback.started', {
				...summarizeSettings(settings),
				audioContextState: audioContext.state,
				audioContextSinkApplied,
				deepFilterApplied,
				deepFilterRequested: profile.deepFilter,
				mediaElementPaused: audioElementRef.current?.paused ?? null,
				mediaElementSinkApplied,
				playbackMode,
				trackLabelPresent: Boolean(sourceTrack.label),
			});
			setIsTesting(true);
			updateLevel();
		} catch (error) {
			logger.error('Error starting mic test', error);
			diagnostics.error('start.failed', {
				...summarizeSettings(settings),
				error: summarizeError(error),
			});
			if (error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
				MediaPermission.markMicrophoneExplicitlyDenied();
				ModalCommands.push(modal(() => <MicrophonePermissionDeniedModal />));
			}
			stop();
		} finally {
			isStartingRef.current = false;
			if (restartPendingRef.current) {
				restartPendingRef.current = false;
				void start();
			}
		}
	}, [settings, updateLevel, stop, micExplicitlyDenied]);
	useEffect(() => {
		if (!isTesting) return;
		if (graphRef.current) {
			graphRef.current.inputGain.gain.value = inputVoiceVolumePercentToGain(settings.inputVolume);
			graphRef.current.outputGain.gain.value = boostedVoiceVolumePercentToTrackVolume(settings.outputVolume);
		}
	}, [isTesting, settings.inputVolume, settings.outputVolume]);
	useEffect(() => {
		if (!isTesting) {
			activeCaptureSignatureRef.current = captureSignature;
			return;
		}
		if (activeCaptureSignatureRef.current === captureSignature) {
			return;
		}
		activeCaptureSignatureRef.current = captureSignature;
		if (isStartingRef.current) {
			restartPendingRef.current = true;
			return;
		}
		void start();
	}, [captureSignature, isTesting, start]);
	useEffect(() => {
		return () => {
			stop();
		};
	}, [stop]);
	return {
		isTesting,
		level,
		start,
		stop,
	};
};
